import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { KILL_SCORE, type KillCounts, createEmptyKillCounts } from '@wooorms/shared';

/**
 * KillScoreDisplay configuration
 */
export interface KillScoreDisplayConfig {
  /** Font size for counts */
  fontSize: number;
  /** Background alpha */
  backgroundAlpha: number;
  /** Padding */
  padding: number;
  /** Corner radius */
  cornerRadius: number;
  /** Spacing between rows */
  rowSpacing: number;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: KillScoreDisplayConfig = {
  fontSize: 14,
  backgroundAlpha: 0.6,
  padding: 10,
  cornerRadius: 6,
  rowSpacing: 4,
};

/**
 * Kill type display info
 */
interface KillTypeInfo {
  key: keyof KillCounts;
  label: string;
  icon: string;
  color: number;
}

/**
 * Ordered list of kill types to display
 */
const KILL_TYPES: KillTypeInfo[] = [
  { key: 'player', label: 'Players', icon: KILL_SCORE.ICONS.PLAYER, color: KILL_SCORE.COLORS.PLAYER },
  { key: 'leviathan', label: 'Leviathan', icon: KILL_SCORE.ICONS.LEVIATHAN, color: KILL_SCORE.COLORS.LEVIATHAN },
  { key: 'apex', label: 'Apex', icon: KILL_SCORE.ICONS.APEX, color: KILL_SCORE.COLORS.APEX },
  { key: 'hunter', label: 'Hunter', icon: KILL_SCORE.ICONS.HUNTER, color: KILL_SCORE.COLORS.HUNTER },
  { key: 'collector', label: 'Collector', icon: KILL_SCORE.ICONS.COLLECTOR, color: KILL_SCORE.COLORS.COLLECTOR },
];

/**
 * KillScoreDisplay - Shows kill counts grouped by type
 *
 * Features:
 * - Displays kills grouped by victim type
 * - Color-coded rows for each type
 * - Icons for visual identification
 * - Compact display near score
 * - Pulse animation on new kills
 */
export class KillScoreDisplay {
  /** Main container */
  public container: Container;

  /** Configuration */
  private config: KillScoreDisplayConfig;

  /** Background graphics */
  private background: Graphics;

  /** Title text */
  private titleText: Text;

  /** Kill count texts by type */
  private killTexts: Map<keyof KillCounts, Text> = new Map();

  /** Current kill counts */
  private kills: KillCounts;

  /** Whether display is visible */
  private _visible: boolean = false;

  /** Pulse states for each type */
  private pulseStates: Map<keyof KillCounts, { timer: number; active: boolean }> = new Map();

  constructor(config: Partial<KillScoreDisplayConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.kills = createEmptyKillCounts();

    // Create container
    this.container = new Container();
    this.container.label = 'kill-score-display';

    // Create background
    this.background = new Graphics();
    this.container.addChild(this.background);

    // Create title
    const titleStyle = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize: 11,
      fill: 0x888888,
      fontWeight: 'bold',
    });
    this.titleText = new Text({ text: 'KILLS', style: titleStyle });
    this.titleText.position.set(this.config.padding, this.config.padding);
    this.container.addChild(this.titleText);

    // Create kill type rows
    let yOffset = this.config.padding + this.titleText.height + 6;

    for (const killType of KILL_TYPES) {
      // Initialize pulse state
      this.pulseStates.set(killType.key, { timer: 0, active: false });

      // Create text for this kill type
      const style = new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: this.config.fontSize,
        fill: killType.color,
        fontWeight: 'bold',
      });

      const text = new Text({
        text: `${killType.icon} 0`,
        style,
      });
      text.position.set(this.config.padding, yOffset);
      text.visible = false; // Hidden until we have a kill of this type
      this.container.addChild(text);
      this.killTexts.set(killType.key, text);

      yOffset += this.config.fontSize + this.config.rowSpacing;
    }

    // Draw initial background
    this.drawBackground();

    // Initially hidden
    this.container.visible = false;
  }

  /**
   * Show the display
   */
  show(): void {
    this._visible = true;
    this.container.visible = true;
  }

  /**
   * Hide the display
   */
  hide(): void {
    this._visible = false;
    this.container.visible = false;
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this._visible;
  }

  /**
   * Set position
   */
  setPosition(x: number, y: number): void {
    this.container.position.set(x, y);
  }

  /**
   * Update kill counts
   */
  setKills(kills: KillCounts): void {
    // Check for new kills and trigger pulses
    for (const killType of KILL_TYPES) {
      const newCount = kills[killType.key];
      const oldCount = this.kills[killType.key];

      if (newCount > oldCount) {
        // New kill - trigger pulse
        const pulseState = this.pulseStates.get(killType.key);
        if (pulseState) {
          pulseState.active = true;
          pulseState.timer = 0;
        }
      }
    }

    this.kills = { ...kills };
    this.updateDisplay();
  }

  /**
   * Add a kill of a specific type
   */
  addKill(type: keyof KillCounts): void {
    this.kills[type]++;

    // Trigger pulse
    const pulseState = this.pulseStates.get(type);
    if (pulseState) {
      pulseState.active = true;
      pulseState.timer = 0;
    }

    this.updateDisplay();
  }

  /**
   * Get current kill counts
   */
  getKills(): KillCounts {
    return { ...this.kills };
  }

  /**
   * Get total kill count
   */
  getTotalKills(): number {
    return (
      this.kills.player +
      this.kills.leviathan +
      this.kills.apex +
      this.kills.hunter +
      this.kills.collector
    );
  }

  /**
   * Reset all kill counts
   */
  reset(): void {
    this.kills = createEmptyKillCounts();
    this.updateDisplay();
  }

  /**
   * Update animation
   * @param dt Delta time in seconds
   */
  update(dt: number): void {
    if (!this._visible) return;

    const pulseDuration = 0.25;
    const pulseScale = 1.2;

    // Update pulse animations for each type
    for (const killType of KILL_TYPES) {
      const pulseState = this.pulseStates.get(killType.key);
      const text = this.killTexts.get(killType.key);

      if (!pulseState || !text) continue;

      if (pulseState.active) {
        pulseState.timer += dt;

        const progress = pulseState.timer / pulseDuration;

        if (progress >= 1) {
          // End pulse
          pulseState.active = false;
          text.scale.set(1, 1);
        } else {
          // Pulse effect
          const pulseProgress = Math.sin(progress * Math.PI);
          const scale = 1 + (pulseScale - 1) * pulseProgress;
          text.scale.set(scale, scale);
        }
      }
    }
  }

  /**
   * Update display text and visibility
   */
  private updateDisplay(): void {
    let yOffset = this.config.padding + this.titleText.height + 6;
    let hasAnyKills = false;

    for (const killType of KILL_TYPES) {
      const text = this.killTexts.get(killType.key);
      if (!text) continue;

      const count = this.kills[killType.key];

      if (count > 0) {
        text.text = `${killType.icon} ${count}`;
        text.visible = true;
        text.position.y = yOffset;
        yOffset += this.config.fontSize + this.config.rowSpacing;
        hasAnyKills = true;
      } else {
        text.visible = false;
      }
    }

    // Hide title if no kills
    this.titleText.visible = hasAnyKills;

    // Redraw background
    this.drawBackground();
  }

  /**
   * Draw background panel
   */
  private drawBackground(): void {
    const { padding, cornerRadius, backgroundAlpha } = this.config;

    // Calculate height based on visible rows
    let height = padding * 2;
    let maxWidth = 60;

    if (this.titleText.visible) {
      height += this.titleText.height + 6;
    }

    for (const killType of KILL_TYPES) {
      const text = this.killTexts.get(killType.key);
      if (text?.visible) {
        height += this.config.fontSize + this.config.rowSpacing;
        maxWidth = Math.max(maxWidth, text.width + padding * 2);
      }
    }

    // Remove last row spacing
    if (this.getTotalKills() > 0) {
      height -= this.config.rowSpacing;
    }

    const g = this.background;
    g.clear();

    // Only draw if we have kills
    if (this.getTotalKills() > 0) {
      g.roundRect(0, 0, maxWidth, height, cornerRadius);
      g.fill({ color: 0x0a0a1a, alpha: backgroundAlpha });
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.background.destroy();
    this.titleText.destroy();
    this.killTexts.forEach((text) => text.destroy());
    this.container.destroy({ children: true });
  }
}
